import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import axiosInstance from '../../axiosConfig';
import '../../assets/CSS/Dashboard.css';
import '../../assets/CSS/FoamBarcodeGenerator.css';

const quantity = value => {
  const number = Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(number) ? new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(number) : (String(value ?? '').trim() || '-');
};

const belongsToTab = (lot, reprint) => reprint
  ? ['existing', 'generated'].includes(lot.status)
  : lot.status === 'pending';

const labelQuantities = lot => [lot.upper, lot.base].map(value => Number(String(value ?? '').replace(/,/g, '').trim()));

export default function FoamBarcodeGenerator() {
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [lots, setLots] = useState([]);
  const [selected, setSelected] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [reprint, setReprint] = useState(false);
  const pendingLotIds = lots.filter(lot => lot.status === 'pending').map(lot => lot.lotId);
  const requestVersion = useRef(0);
  const downloadInProgress = useRef(false);
  const [lastChecked, setLastChecked] = useState(null);
  const switchTab = nextReprint => {
    if (downloading || nextReprint === reprint) return;
    requestVersion.current += 1;
    setReprint(nextReprint);
    setSelected({});
    setSearch('');
  };

  const loadLots = useCallback(async (showLoader = false) => {
    if (downloadInProgress.current) return;
    const version = ++requestVersion.current;
    if (showLoader) setLoading(true);
    try {
      const response = await axiosInstance.get('/api/foam-cutting-barcodes/lots', { timeout: 30000 });
      const incomingLots = response.data?.lots || [];
      if (version !== requestVersion.current) return;
      setLots(incomingLots);
      setLastChecked(new Date());
      setSelected(current => Object.fromEntries(
        Object.entries(current).filter(([id]) => incomingLots.some(lot => lot.lotId === id && belongsToTab(lot, reprint)))
      ));
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Lot IDs load nahi ho paye.');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [reprint]);

  useEffect(() => {
    loadLots(true);
    const refreshTimer = window.setInterval(() => loadLots(false), 60000);
    return () => window.clearInterval(refreshTimer);
  }, [loadLots]);

  const filteredLots = useMemo(() => {
    const value = search.trim().toLowerCase();
    return lots.filter(lot => belongsToTab(lot, reprint) && (!value || lot.lotId.toLowerCase().includes(value)));
  }, [lots, search, reprint]);

  const selectedLots = useMemo(() => lots.filter(lot => selected[lot.lotId] && belongsToTab(lot, reprint)), [lots, selected, reprint]);
  const invalidQuantity = selectedLots.some(lot => {
    const values = labelQuantities(lot);
    return values.some(value => !Number.isSafeInteger(value) || value < 0) || !values.some(value => value > 0);
  });
  const totalStickers = selectedLots.reduce((sum, lot) => sum + labelQuantities(lot).reduce((count, value) => count + (Number.isSafeInteger(value) && value > 0 ? value : 0), 0), 0);
  const allVisibleSelected = filteredLots.length > 0 && filteredLots.every(lot => selected[lot.lotId]);

  const toggleLot = lotId => setSelected(current => {
    const next = { ...current };
    if (next[lotId]) delete next[lotId];
    else next[lotId] = 1;
    return next;
  });

  const toggleAllVisible = () => setSelected(current => {
    const next = { ...current };
    filteredLots.forEach(lot => {
      if (allVisibleSelected) delete next[lot.lotId];
      else if (!next[lot.lotId]) next[lot.lotId] = 1;
    });
    return next;
  });

  const downloadPdf = async () => {
    if (downloadInProgress.current) return;
    if (!selectedLots.length) return setError('PDF download karne ke liye kam se kam ek Lot ID select karein.');
    if (invalidQuantity || totalStickers > 5000 || selectedLots.length > 500) return;
    downloadInProgress.current = true;
    requestVersion.current += 1;
    setError('');
    let generationStarted = false;
    try {
      const confirmation = await Swal.fire({
        title: reprint ? 'PDF dobara download karein?' : 'PDF download karein?',
        text: `${selectedLots.length} LOTs ke ${quantity(totalStickers)} Upper / Base labels (${quantity(totalStickers)} PDF pages) generate honge. Upper: LOCATION UPP, Base: LOCATION BAS.${reprint ? '' : ' PDF generate hone par selected LOTs Existing Lots tab mein chale jayenge.'}`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: reprint ? 'Haan, Download Again' : 'Haan, Download PDF',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#396759',
      });
      if (!confirmation.isConfirmed) return;
      generationStarted = true;
      setDownloading(true);
      Swal.fire({
        title: 'Barcode PDF ban raha hai...',
        text: `${quantity(totalStickers)} stickers generate ho rahe hain. Download shuru hone tak please wait karein aur page refresh na karein.`,
        backdrop: 'rgba(243, 246, 248, 0.97)',
        customClass: { container: 'foam-pdf-loading-overlay', popup: 'foam-pdf-loading-popup' },
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        didOpen: () => Swal.showLoading(),
      });
      const response = await axiosInstance.post('/api/foam-cutting-barcodes/pdf', {
        reprint,
        lots: selectedLots.map(lot => ({ lotId: lot.lotId })),
      }, { responseType: 'blob' });
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] || 'FoamCutting_Barcodes.pdf';
      const url = window.URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      const downloadedIds = new Set(selectedLots.map(lot => lot.lotId));
      setLots(current => current.map(lot => downloadedIds.has(lot.lotId) ? { ...lot, status: 'generated' } : lot));
      setSelected(current => Object.fromEntries(Object.entries(current).filter(([lotId]) => !downloadedIds.has(lotId))));
    } catch (requestError) {
      let message = 'Barcode PDF generate nahi ho paya.';
      if (requestError.response?.data instanceof Blob) {
        try { message = JSON.parse(await requestError.response.data.text()).message || message; } catch (_error) { /* retain fallback */ }
      }
      setError(message);
    } finally {
      downloadInProgress.current = false;
      if (generationStarted) {
        Swal.update({ title: 'LOT list refresh ho rahi hai...', text: 'Please wait...' });
      }
      try {
        await loadLots(false);
      } finally {
        setDownloading(false);
        if (generationStarted) Swal.close();
      }
    }
  };

  return (
    <div className="d-flex dashboard foam-barcode-layout">
      <div className={isSidebarVisible ? 'sidebar-container shadow-sm' : 'sidebar-hidden'}><Sidebar /></div>
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <Header toggleSidebar={() => setIsSidebarVisible(value => !value)} isSidebarVisible={isSidebarVisible} />
        <main className="main-container foam-barcode-main">
          <section className="foam-barcode-hero">
            <div><span className="foam-barcode-eyebrow">100 x 150 mm sticker</span><h1>Foam Cutting Barcodes</h1><p>Lot IDs select karein aur scanner-ready Code 128 PDF download karein.</p></div>
            <div className="foam-barcode-summary"><strong>{selectedLots.length}</strong><span>Lots selected</span><strong>{totalStickers}</strong><span>PDF pages</span></div>
          </section>

          {error && <div className="alert alert-danger d-flex justify-content-between align-items-center"><span>{error}</span><button className="btn btn-sm btn-outline-danger" onClick={() => setError('')}>Close</button></div>}

          {pendingLotIds.length > 0 && <div className="foam-new-lot-alert" role="status">
            <div><i className="bi bi-printer-fill" /><strong>{pendingLotIds.length} new LOT PDF pending</strong><span>PDF generate hone par LOT Existing Lots tab mein aa jayega.</span></div>
            <button type="button" onClick={() => loadLots(false)}><i className="bi bi-arrow-clockwise" /> Refresh</button>
          </div>}

          <section className="foam-barcode-card">
            <div className="foam-lot-tabs" role="tablist" aria-label="Lot category">
              {[false, true].map(isExisting => <button key={String(isExisting)} type="button" role="tab" id={isExisting ? 'existing-lots-tab' : 'new-lots-tab'} aria-controls="foam-lots-panel" aria-selected={reprint === isExisting} disabled={downloading} tabIndex={reprint === isExisting ? 0 : -1} className={`foam-lot-tab ${reprint === isExisting ? 'active' : ''}`} onClick={() => switchTab(isExisting)} onKeyDown={event => {
                if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
                  event.preventDefault();
                  const next = event.key === 'Home' ? false : event.key === 'End' ? true : !isExisting;
                  switchTab(next);
                  document.getElementById(next ? 'existing-lots-tab' : 'new-lots-tab')?.focus();
                }
              }}>
                <i className={`bi ${isExisting ? 'bi-archive' : 'bi-stars'}`} aria-hidden="true" />
                <span>{isExisting ? 'Existing Lots / Reprint' : 'New Lots'}</span>
                <span className="foam-tab-count">{loading ? '...' : lots.filter(lot => belongsToTab(lot, isExisting)).length}</span>
              </button>)}
            </div>
            <div id="foam-lots-panel" role="tabpanel" aria-labelledby={reprint ? 'existing-lots-tab' : 'new-lots-tab'}>
            <div className="p-3 d-flex align-items-center gap-3 flex-wrap">
              <button type="button" className="btn btn-sm btn-outline-success" disabled={downloading} onClick={() => loadLots(false)}>Refresh</button>
              <span className="small text-muted">{reprint ? 'Purane LOTs ka PDF dobara chahiye? Required LOTs select karein.' : 'Naye LOTs select karein aur PDF download karein.'}</span>
            </div>
            <div className="foam-barcode-toolbar">
              <div className="foam-barcode-search"><i className="bi bi-search" /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Lot ID search karein..." aria-label="Search Lot ID" /></div>
              <button type="button" className="btn btn-dark foam-barcode-download" disabled={!selectedLots.length || downloading || invalidQuantity || totalStickers > 5000 || selectedLots.length > 500} onClick={downloadPdf}>
                {downloading ? <><span className="spinner-border spinner-border-sm me-2" />Generating...</> : <><i className="bi bi-file-earmark-pdf me-2" />{reprint ? 'Download Again' : 'Download PDF'}</>}
              </button>
            </div>
            <div className="foam-barcode-sync"><i className="bi bi-broadcast" /> New LOT check har 1 minute mein hota hai{lastChecked ? ` - Last checked ${lastChecked.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : ''}</div>
            <div className="foam-barcode-sync">Har sheet ka 1 sticker (1 PCS). Upper: LOCATION: UPP | Base: LOCATION: BAS. Zero / blank quantity ka label nahi banega.</div>
            {invalidQuantity && <div className="alert alert-warning mx-3 mt-3 mb-0">Selected LOT ki Upper / Base sheet quantity non-negative whole number honi chahiye, aur kam se kam ek quantity zero se zyada honi chahiye.</div>}
            {(totalStickers > 5000 || selectedLots.length > 500) && <div className="alert alert-warning mx-3 mt-3 mb-0">Ek PDF mein maximum 500 LOTs aur 5000 labels allowed hain. Kam LOTs select karein.</div>}

            {loading ? <div className="foam-barcode-state"><span className="spinner-border" /><strong>Lot IDs load ho rahe hain...</strong></div> : (
              <div className="table-responsive foam-barcode-table-wrap">
                <table className="table align-middle mb-0 foam-barcode-table">
                  <colgroup><col style={{ width: 145 }} /><col /><col style={{ width: 120 }} /><col style={{ width: 120 }} /></colgroup>
                  <thead><tr><th><label className="foam-select-all"><input className="form-check-input" type="checkbox" checked={allVisibleSelected} disabled={!filteredLots.length} onChange={toggleAllVisible} /><span>Select All</span></label></th><th>Lot ID</th><th>Upper</th><th>Base</th></tr></thead>
                  <tbody>
                    {filteredLots.map(lot => {
                      const isSelected = Boolean(selected[lot.lotId]);
                      const isPending = pendingLotIds.includes(lot.lotId);
                      return <tr key={lot.lotId} className={`${isSelected ? 'selected' : ''} ${isPending ? 'new-lot' : ''}`.trim()}>
                        <td><input className="form-check-input" type="checkbox" checked={isSelected} onChange={() => toggleLot(lot.lotId)} aria-label={`Select ${lot.lotId}`} /></td>
                        <td><button type="button" className="foam-lot-button" onClick={() => toggleLot(lot.lotId)}>{lot.lotId}</button></td>
                        <td><strong>{quantity(lot.upper)}</strong></td>
                        <td><strong>{quantity(lot.base)}</strong></td>
                      </tr>;
                    })}
                    {!filteredLots.length && <tr><td colSpan="4"><div className="foam-barcode-state"><i className="bi bi-inbox" /><strong>{search.trim() ? 'Is tab mein matching LOT nahi mila.' : reprint ? 'Abhi koi existing LOT nahi hai.' : 'Sab done! Koi naya LOT pending nahi hai.'}</strong>{!reprint && !search.trim() && <button className="btn btn-outline-success" type="button" disabled={downloading} onClick={() => switchTab(true)}>Existing Lots dekhein</button>}</div></td></tr>}
                  </tbody>
                </table>
              </div>
            )}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
